/**
 * THE HANDOVER: what "Open in Assistant" takes from a drill's chat to the
 * Assistant page (owner decisions 21 and 22).
 *
 * The owner, 2026-10-17 ~21:45 EAT, on the deployed close10 engine: "when I
 * open the chat from a drill panel then click open assistant - it opens like
 * the above attached" — the page About nothing, an empty conversation, and the
 * one card "Investigate this web client · Needs a web client". The drill's
 * panel handed over a process exec id and its surface, and nothing else: a web
 * client, a device's name, a VPN device's, a playbook run, a KPI or the plane a
 * panel is about were dropped on the way, and so was the conversation already
 * in the drill's chat.
 *
 * Three things go with the operator now, and only these:
 *  - THE SUBJECT, typed (AssistantSubjectRef): what the About chip names, what
 *    "← Back to" adds after the page's name, and — for the kinds the server
 *    reads — the subject string a task needs (execIdOf), so the cards made for
 *    it run on the page as they do in the drill;
 *  - THE PAGE AND DRILL to return to: the route's opener on the SOC route, or
 *    on another document the drill's own address (drillAddress);
 *  - THE CONVERSATION in the drill's chat (DrillConversation): its id and its
 *    questions and answers, which continue on the page — on the engine, where
 *    history is off, as the conversation this tab keeps (decision 22).
 *
 * None of it is authority: the subject is framing (api.ts AssistantSurface),
 * the conversation is what this tab already showed the operator, and every
 * record that crosses a document is read strictly (carry.ts) and bound to this
 * tab's customer, account and session.
 */
import type { ChatMessage } from "./chatApi";
import { KEPT_KEY, MAX_FROM, parseChatMessage } from "./keptConversation";
import { execIdPid } from "../soc/format";
// Called only inside functions (carry.ts reads this module the same way), so the two never meet half-loaded.
import { parseBack, type CarryBack } from "./carry";

/** What a drill's chat is about, by kind: the one value every drill hands over. */
export type AssistantSubjectRef =
  /** A process, by its exec id (and its binary, where the drill knows it). */
  | { kind: "process"; execId: string; name?: string }
  /** An alert, by its id and title; its subject string (a process, a device, a change…) when it has one. */
  | { kind: "alert"; id: string; title: string; execId?: string }
  /** A node of the correlation graph, by the label its inspector heads with. */
  | { kind: "graph-node"; label: string; execId?: string }
  /** A device, by its MAC (and its host name, where known). */
  | { kind: "device"; mac: string; name?: string }
  /** A VPN device, by its peer number (and its label, where known). */
  | { kind: "vpn-peer"; peer: number; name?: string }
  /** A web client, by its key: an IPv4 address or an IPv6 /64. */
  | { kind: "web-client"; client: string }
  /** A policy change record, by its id. */
  | { kind: "change"; id: string; title?: string }
  /** One run of a playbook. */
  | { kind: "playbook-run"; playbook: string; run: string }
  /** A KPI tile's drill, by its title and the window it counts. */
  | { kind: "kpi"; title: string; window?: string }
  /** A whole page's own chat (no drill): the edge's sites, remote access, the firewall, a link. */
  | { kind: "view"; label: string };

export type AssistantSubjectKind = AssistantSubjectRef["kind"];

/**
 * The subject string the server reads for this subject — the ledger's own
 * spelling, the one a task's `needs` checks (api.ts taskWithheld) — or
 * undefined for a subject the server has no string for (a run, a KPI, a page).
 */
export function execIdOf(ref?: AssistantSubjectRef | null): string | undefined {
  if (!ref) return undefined;
  switch (ref.kind) {
    case "process":
      return ref.execId;
    case "alert":
    case "graph-node":
      return ref.execId || undefined;
    case "device":
      return `device:${ref.mac}`;
    case "vpn-peer":
      return `vpn:${ref.peer}`;
    case "web-client":
      return `web:${ref.client}`;
    case "change":
      return `change:${ref.id}`;
    default:
      return undefined;
  }
}

/**
 * One string per subject: two handovers about the same subject are the same
 * conversation's (AssistantPage continues it rather than ending it).
 */
export function subjectKey(ref: AssistantSubjectRef): string {
  switch (ref.kind) {
    case "process":
      return `process:${ref.execId}`;
    case "alert":
      return `alert:${ref.id}`;
    case "graph-node":
      return `graph-node:${ref.execId ?? ref.label}`;
    case "device":
      return `device:${ref.mac.toLowerCase()}`;
    case "vpn-peer":
      return `vpn-peer:${ref.peer}`;
    case "web-client":
      return `web-client:${ref.client}`;
    case "change":
      return `change:${ref.id}`;
    case "playbook-run":
      return `playbook-run:${ref.playbook}/${ref.run}`;
    case "kpi":
      return `kpi:${ref.title}`;
    case "view":
      return `view:${ref.label}`;
  }
}

const baseName = (path: string) => path.split("/").filter(Boolean).pop() ?? path;

function clip(text: string, max: number): string {
  const t = text.trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

function processName(ref: { execId: string; name?: string }): string {
  const name = ref.name ? baseName(ref.name) : "";
  const pid = execIdPid(ref.execId);
  return [name, pid ? `pid ${pid}` : ""].filter(Boolean).join(" · ") || ref.execId.slice(0, 12);
}

/**
 * The About chip's words: the kind, and the subject's name as the drill shows
 * it. A page's own chat has no kind word: "About the edge's sites".
 */
export function subjectChip(ref: AssistantSubjectRef): { kind: string; name: string } {
  switch (ref.kind) {
    case "process":
      return { kind: "process", name: processName(ref) };
    case "alert":
      return { kind: "alert", name: ref.title };
    case "graph-node":
      return { kind: "graph node", name: ref.label };
    case "device":
      return { kind: "device", name: ref.name ? `${ref.name} (${ref.mac})` : ref.mac };
    case "vpn-peer":
      return { kind: "VPN device", name: ref.name ? `${ref.name} · #${ref.peer}` : `#${ref.peer}` };
    case "web-client":
      return { kind: "web client", name: ref.client };
    case "change":
      return { kind: "policy change", name: ref.title ? `${ref.title} (${ref.id})` : ref.id };
    case "playbook-run":
      return { kind: "playbook", name: `${ref.playbook} · run ${ref.run}` };
    case "kpi":
      return { kind: "KPI", name: ref.window ? `${ref.title} · ${ref.window}` : ref.title };
    case "view":
      return { kind: "", name: ref.label };
  }
}

/**
 * What "← Back to <page> · <drill>" adds after the page's name, or null where
 * the page itself is the subject (a KPI's drill page, a page's own chat).
 */
export function drillWords(ref?: AssistantSubjectRef | null): string | null {
  if (!ref) return null;
  switch (ref.kind) {
    case "process":
      return `process ${clip(ref.name ? baseName(ref.name) : processName(ref), 40)}`;
    case "alert":
      return `alert ${clip(ref.title, 48)}`;
    case "graph-node":
      return `graph node ${clip(ref.label, 48)}`;
    case "device":
      return `device ${clip(ref.name || ref.mac, 40)}`;
    case "vpn-peer":
      return `VPN device ${clip(ref.name || `#${ref.peer}`, 40)}`;
    case "web-client":
      return `web client ${ref.client}`;
    case "change":
      return `change ${clip(ref.id, 40)}`;
    case "playbook-run":
      return `run ${clip(ref.run, 24)}`;
    default:
      return null;
  }
}

/** "← Back to" words: the page, then the drill on it. */
export function backLabel(page: string, ref?: AssistantSubjectRef | null): string {
  const drill = drillWords(ref);
  return drill ? `${page} · ${drill}` : page;
}

/**
 * Where "← Back to" goes on one of the plane documents, so it returns to the
 * DRILL and not only to the page: Processes opens a process's drill from
 * `?exec=`, Devices a device's from `#device=`, VPN a peer's from `#peer=`
 * (each document's own deep link). Anywhere else, the address it was asked on.
 */
export function drillAddress(ref: AssistantSubjectRef | undefined | null, here: { pathname: string; search: string; hash: string }): string {
  const asked = here.pathname + here.search + here.hash;
  if (!ref) return asked;
  if (here.pathname === "/choke" && ref.kind === "process") {
    const query = new URLSearchParams(here.search);
    query.set("exec", ref.execId);
    return `/choke?${query.toString()}${here.hash}`;
  }
  if (here.pathname === "/devices" && ref.kind === "device") {
    const fragment = new URLSearchParams(here.hash.replace(/^#/, ""));
    fragment.set("device", ref.mac);
    return `/devices${here.search}#${fragment.toString()}`;
  }
  if (here.pathname === "/vpn" && ref.kind === "vpn-peer") {
    const named = new URLSearchParams(here.hash.replace(/^#/, "")).get("peer");
    return named === String(ref.peer) ? asked : `/vpn${here.search}#peer=${ref.peer}`;
  }
  return asked;
}

/** The questions and answers in a drill's chat, handed to the page. */
export interface DrillConversation {
  /** The drill chat's own id: handed over again, the page knows it is the same conversation. */
  id: string;
  /** This tab's customer selection when the drill's chat began (selectedTenantNow), or null. */
  tenant: string | null;
  messages: ChatMessage[];
}

/** The most turns a handover carries (a drill's chat holds a few; this bounds a record in storage). */
export const MAX_HANDED_MESSAGES = 40;

const CONVERSATION_ID = /^[A-Za-z0-9._:-]{1,64}$/;

/** A fresh drill conversation id. Not a secret and never sent to a server: it names a chat in this tab. */
export function newConversationId(): string {
  return `drill-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The page's conversation, the handover's added: every turn of the drill's chat
 * the page does not already show (by id), after the page's own. The drill's
 * chat asked again after Back and Open adds its new turns; nothing the page
 * said since is lost.
 */
export function mergeTurns(current: ChatMessage[], handed: ChatMessage[]): ChatMessage[] {
  const seen = new Set(current.map((m) => m.id));
  const added = handed.filter((m) => !seen.has(m.id));
  return added.length ? [...current, ...added] : current;
}

/**
 * Whether a handover continues the conversation on the page: it is a drill
 * conversation the page already continues, or it is about the subject the
 * page's conversation was handed over about (a drill opened again — on
 * another document, after a reload — starts a new drill chat about the same
 * thing). Anything else is another conversation (AssistantPage says what
 * becomes of the one on screen).
 *
 * Never two subjects in one conversation (review r2): a drill chat's id about
 * another subject than the page's conversation is another conversation. A
 * drill chat begins a new id when its subject changes (useAssistant), so the
 * two only differ where something has gone wrong; this is the second lock.
 */
export function continuesConversation(
  page: { from: readonly string[]; about: string | null; turns: number },
  handed: { id: string; about: string | null }
): boolean {
  const otherSubject = page.about !== null && handed.about !== null && page.about !== handed.about;
  if (page.from.includes(handed.id)) return !otherSubject;
  return page.turns > 0 && page.about !== null && handed.about !== null && page.about === handed.about;
}

/**
 * THE PREVIOUS CONVERSATION (review r2; owner decision 22): the one on the
 * page when a drill chat with turns about another subject began a new one.
 * Where history keeps conversations (the control plane) it is in the list;
 * anywhere else the page keeps it one press away ("Go back to it"), and on the
 * engine, where history is off, it rides in the record kept for the tab
 * (keptConversation.ts) — so the record's own rules hold for it unchanged:
 * this tab's customer, account and session, cleared by sign-out, a customer
 * switch and New conversation, dropped whole when anything in it is malformed.
 */
export interface EarlierConversation {
  messages: ChatMessage[];
  /** What it was about (subjectKey), when a drill handed it over. */
  about?: string;
  /** The drill conversations it continued (their ids), newest last. */
  from?: string[];
  /**
   * What it was about when it was left (r4): the About chip's subject and the
   * exec id its questions went with, so "Go back to it" after a reload brings
   * them back rather than clearing them (keptAbout writes it, bounded).
   */
  subject?: KeptSubject;
  /** Its "← Back to" target, where that has a console address (carry.ts parseBack). */
  back?: CarryBack;
}

/** An earlier conversation read strictly: every turn well formed, `about` and `from` bounded as a kept record's own, or nothing. */
export function parseEarlier(value: unknown): EarlierConversation | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  if (!Array.isArray(o.messages) || o.messages.length === 0) return null;
  const messages: ChatMessage[] = [];
  for (const m of o.messages) {
    const parsed = parseChatMessage(m);
    if (!parsed) return null;
    messages.push(parsed);
  }
  if (!(o.about === undefined || (typeof o.about === "string" && o.about.length <= 600))) return null;
  if (!(o.from === undefined || (Array.isArray(o.from) && o.from.length <= MAX_FROM && o.from.every((id) => typeof id === "string" && CONVERSATION_ID.test(id))))) {
    return null;
  }
  // What it was about, as strictly: a subject or a Back target that does not parse drops it whole.
  const subject = o.subject === undefined ? undefined : parseKeptSubject(o.subject);
  if (subject === null) return null;
  const back = o.back === undefined ? undefined : parseBack(o.back);
  if (back === null) return null;
  return {
    messages,
    ...(typeof o.about === "string" && o.about ? { about: o.about } : {}),
    ...(Array.isArray(o.from) && o.from.length ? { from: [...(o.from as string[])] } : {}),
    ...(subject ? { subject } : {}),
    ...(back ? { back } : {})
  };
}

/**
 * The earlier conversation riding in the record this tab keeps, read as
 * storage holds it: undefined where the record carries none (or there is no
 * record), null where it carries one that is malformed — the caller drops the
 * record whole, as keptConversation.ts does with any malformed part of it.
 */
export function readKeptEarlier(): EarlierConversation | null | undefined {
  let value: unknown;
  try {
    const raw = window.sessionStorage.getItem(KEPT_KEY);
    if (!raw) return undefined;
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!value || typeof value !== "object" || !("earlier" in value)) return undefined;
  return parseEarlier((value as { earlier: unknown }).earlier);
}

/** The longest a subject's text field may be (a MAC or a client key is shorter). */
const TEXT_MAX = 256;
const SHORT_MAX = 64;

const clipField = (value: string, max: number) => value.trim().slice(0, max);

/**
 * A subject as a drill hands it, made to fit the rules it is read back by:
 * every text field trimmed and clipped to its limit, so a long alert title or
 * binary path never makes the record it travels in unreadable (parseCarry
 * drops a record whole). A subject missing what its kind needs is no subject.
 */
export function normalizeSubject(ref?: AssistantSubjectRef | null): AssistantSubjectRef | undefined {
  if (!ref) return undefined;
  const clip = (value: unknown, max = TEXT_MAX) => (typeof value === "string" ? clipField(value, max) : value);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(ref)) {
    out[key] = key === "kind" || key === "peer" ? value : clip(value, key === "mac" || key === "client" || key === "run" ? SHORT_MAX : TEXT_MAX);
    if (out[key] === "") delete out[key];
  }
  return parseSubjectRef(out) ?? undefined;
}

const isText = (v: unknown, max = TEXT_MAX): v is string => typeof v === "string" && v.trim() !== "" && v.length <= max;
const isOptionalText = (v: unknown, max = 256): boolean => v === undefined || v === null || isText(v, max);

/** A subject read strictly: a known kind, every field of its type and length, or nothing. */
export function parseSubjectRef(value: unknown): AssistantSubjectRef | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  const opt = (key: string): Record<string, string> => (isText(o[key]) ? { [key]: o[key] as string } : {});
  switch (o.kind) {
    case "process":
      return isText(o.execId) && isOptionalText(o.name) ? { kind: "process", execId: o.execId, ...opt("name") } : null;
    case "alert":
      return isText(o.id) && isText(o.title) && isOptionalText(o.execId) ? { kind: "alert", id: o.id, title: o.title, ...opt("execId") } : null;
    case "graph-node":
      return isText(o.label) && isOptionalText(o.execId) ? { kind: "graph-node", label: o.label, ...opt("execId") } : null;
    case "device":
      return isText(o.mac, 64) && isOptionalText(o.name) ? { kind: "device", mac: o.mac, ...opt("name") } : null;
    case "vpn-peer":
      return typeof o.peer === "number" && Number.isInteger(o.peer) && o.peer > 0 && isOptionalText(o.name)
        ? { kind: "vpn-peer", peer: o.peer, ...opt("name") }
        : null;
    case "web-client":
      return isText(o.client, 64) ? { kind: "web-client", client: o.client } : null;
    case "change":
      return isText(o.id) && isOptionalText(o.title) ? { kind: "change", id: o.id, ...opt("title") } : null;
    case "playbook-run":
      return isText(o.playbook) && isText(o.run, 64) ? { kind: "playbook-run", playbook: o.playbook, run: o.run } : null;
    case "kpi":
      return isText(o.title) && isOptionalText(o.window) ? { kind: "kpi", title: o.title, ...opt("window") } : null;
    case "view":
      return isText(o.label) ? { kind: "view", label: o.label } : null;
    default:
      return null;
  }
}

/** A drill conversation read strictly: its id, its customer stamp and every turn well formed, or nothing. */
export function parseDrillConversation(value: unknown): DrillConversation | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  if (typeof o.id !== "string" || !CONVERSATION_ID.test(o.id)) return null;
  if (!(o.tenant === null || typeof o.tenant === "string")) return null;
  if (!Array.isArray(o.messages) || o.messages.length > MAX_HANDED_MESSAGES) return null;
  const messages: ChatMessage[] = [];
  for (const m of o.messages) {
    const parsed = parseChatMessage(m);
    if (!parsed) return null;
    messages.push(parsed);
  }
  return { id: o.id, tenant: (o.tenant as string | null) ?? null, messages };
}

/** The subject a previous conversation was about, as the record kept for the tab names it (r4). */
export interface KeptSubject {
  /** The exec id its questions went with. */
  execId?: string;
  scopeLabel?: string;
  /** What the About chip says. */
  ref?: AssistantSubjectRef;
}

/** A kept subject read strictly: each field of its type and length, at least one of them, or nothing. */
function parseKeptSubject(value: unknown): KeptSubject | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  if (!(o.execId === undefined || isText(o.execId)) || !(o.scopeLabel === undefined || isText(o.scopeLabel))) return null;
  const ref = o.ref === undefined ? undefined : parseSubjectRef(o.ref);
  if (ref === null) return null;
  if (o.execId === undefined && o.scopeLabel === undefined && ref === undefined) return null;
  return {
    ...(typeof o.execId === "string" ? { execId: o.execId } : {}),
    ...(typeof o.scopeLabel === "string" ? { scopeLabel: o.scopeLabel } : {}),
    ...(ref ? { ref } : {})
  };
}

/**
 * What a previous conversation was about, made to fit the rules parseEarlier
 * reads it back by (r4), so it never sinks the record it rides in: the
 * subject normalized, an exec id kept whole or not at all (a clipped id names
 * another process), and the Back target only where it has a console address
 * (the provider's AssistantReturn.href).
 */
export function keptAbout(
  about?: { subject?: { execId?: string; scopeLabel?: string; ref?: AssistantSubjectRef }; back?: { label: string; href?: string } | null } | null
): Pick<EarlierConversation, "subject" | "back"> {
  if (!about) return {};
  const s = about.subject;
  const ref = normalizeSubject(s?.ref);
  const execId = typeof s?.execId === "string" && isText(s.execId) ? s.execId : undefined;
  const scopeLabel = typeof s?.scopeLabel === "string" ? clipField(s.scopeLabel, TEXT_MAX) : "";
  const subject: KeptSubject = { ...(execId ? { execId } : {}), ...(scopeLabel ? { scopeLabel } : {}), ...(ref ? { ref } : {}) };
  const back = about.back?.href ? parseBack({ label: about.back.label, href: about.back.href }) : null;
  return { ...(Object.keys(subject).length ? { subject } : {}), ...(back ? { back } : {}) };
}

/**
 * WHAT THE CONVERSATION ON SCREEN IS ABOUT (r4b): its About chip's subject,
 * the exec id its questions go with, and its Back target, riding in the record
 * this tab keeps as `now` (keptAbout writes it), so a reload brings them back
 * with the conversation instead of sending the next question unscoped over the
 * subject's turns. It rides in that record, so the record's own rules hold for
 * it: this tab's customer, account and session; dropped whole when malformed.
 */
export type KeptAbout = Pick<EarlierConversation, "subject" | "back">;

/** A `now` read strictly: a subject and a Back that each parse, at least one of them, or nothing. */
export function parseKeptAbout(value: unknown): KeptAbout | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  const subject = o.subject === undefined ? undefined : parseKeptSubject(o.subject);
  if (subject === null) return null;
  const back = o.back === undefined ? undefined : parseBack(o.back);
  if (back === null) return null;
  if (!subject && !back) return null;
  return { ...(subject ? { subject } : {}), ...(back ? { back } : {}) };
}

/**
 * The `now` riding in the record this tab keeps, as readKeptEarlier reads
 * `earlier`: undefined where the record carries none (or there is no record),
 * null where it carries a malformed one — the caller drops the record whole.
 */
export function readKeptNow(): KeptAbout | null | undefined {
  let value: unknown;
  try {
    const raw = window.sessionStorage.getItem(KEPT_KEY);
    if (!raw) return undefined;
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!value || typeof value !== "object" || !("now" in value)) return undefined;
  return parseKeptAbout((value as { now: unknown }).now);
}
